import { useQuery, useQueries } from '@tanstack/react-query'
import { comparisonService } from '@/services/comparisonService'
import type { ListDimensionType, ListResultItem } from '@/types/comparison'

/**
 * Hook for fetching fixed lists of subjects.
 *
 * Supports lists for: platform, country, ageGroup, emotion, category.
 *
 * @param type - Dimension type to list
 * @param options - Optional configuration
 * @returns React Query result with list items
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useSubjectLists('platform')
 *
 * return (
 *   <select>
 *     {data?.data.map((item) => (
 *       <option key={item.id} value={item.id}>
 *         {item.name}
 *       </option>
 *     ))}
 *   </select>
 * )
 * ```
 */
export function useSubjectLists(
  type: ListDimensionType,
  options?: {
    /**
     * Enable/disable the query (default: true).
     */
    enabled?: boolean

    /**
     * Stale time in milliseconds (default: 1 hour).
     * Fixed lists rarely change, so cache aggressively.
     */
    staleTime?: number

    /**
     * Cache time in milliseconds (default: 24 hours).
     * How long to keep the data in cache when inactive.
     */
    cacheTime?: number
  }
) {
  const {
    enabled = true,
    staleTime = 60 * 60 * 1000, // 1 hour
    cacheTime = 24 * 60 * 60 * 1000, // 24 hours
  } = options || {}

  return useQuery({
    queryKey: ['subjectList', type],
    queryFn: async () => {
      return await comparisonService.listSubjects({ type })
    },
    enabled,
    staleTime,
    gcTime: cacheTime, // React Query v5 uses gcTime instead of cacheTime
    // Retry on failure
    retry: 2,
    retryDelay: 1000,
  })
}

/**
 * Hook for fetching multiple fixed lists at once.
 *
 * Useful for preloading all dimension options.
 *
 * @param types - Array of dimension types to list
 * @returns Object mapping types to React Query results
 *
 * @example
 * ```tsx
 * const lists = useMultipleSubjectLists(['platform', 'ageGroup', 'emotion'])
 *
 * const platforms = lists.platform.data?.data ?? []
 * const ageGroups = lists.ageGroup.data?.data ?? []
 * const emotions = lists.emotion.data?.data ?? []
 *
 * const isLoading = lists.platform.isLoading || lists.ageGroup.isLoading
 * ```
 */
export function useMultipleSubjectLists(types: ListDimensionType[]) {
  const queries = useQueries({
    queries: types.map((type) => ({
      queryKey: ['subjectList', type],
      queryFn: async () => {
        return await comparisonService.listSubjects({ type })
      },
      staleTime: 60 * 60 * 1000, // 1 hour
      gcTime: 24 * 60 * 60 * 1000, // 24 hours
      retry: 2,
      retryDelay: 1000,
    })),
  })

  return types.reduce(
    (acc, type, index) => {
      acc[type] = queries[index]
      return acc
    },
    {} as Record<ListDimensionType, ReturnType<typeof useSubjectLists>>
  )
}

/**
 * Helper: Extract list items from query response.
 *
 * @param data - Query response data
 * @returns Array of list result items, or empty array if no data
 */
export function getListItems(
  data: ReturnType<typeof useSubjectLists>['data']
): ListResultItem[] {
  return data?.data ?? []
}

/**
 * Helper: Get specific list item by ID.
 *
 * @param data - Query response data
 * @param id - Item ID to find
 * @returns List item if found, undefined otherwise
 */
export function getListItemById(
  data: ReturnType<typeof useSubjectLists>['data'],
  id: number | string
): ListResultItem | undefined {
  return data?.data.find((item) => item.id === id)
}

/**
 * Hook for platforms list (convenience wrapper).
 *
 * @example
 * ```tsx
 * const { data, isLoading } = usePlatformsList()
 * const platforms = data?.data ?? []
 * ```
 */
export function usePlatformsList(options?: Parameters<typeof useSubjectLists>[1]) {
  return useSubjectLists('platform', options)
}

/**
 * Hook for countries list (convenience wrapper).
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useCountriesList()
 * const countries = data?.data ?? []
 * ```
 */
export function useCountriesList(options?: Parameters<typeof useSubjectLists>[1]) {
  return useSubjectLists('country', options)
}

/**
 * Hook for age groups list (convenience wrapper).
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useAgeGroupsList()
 * const ageGroups = data?.data ?? []
 * ```
 */
export function useAgeGroupsList(options?: Parameters<typeof useSubjectLists>[1]) {
  return useSubjectLists('ageGroup', options)
}

/**
 * Hook for emotions list (convenience wrapper).
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useEmotionsList()
 * const emotions = data?.data ?? []
 * ```
 */
export function useEmotionsList(options?: Parameters<typeof useSubjectLists>[1]) {
  return useSubjectLists('emotion', options)
}

/**
 * Hook for categories list (convenience wrapper).
 *
 * Note: Categories table is not implemented in backend, will return empty array.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useCategoriesList()
 * const categories = data?.data ?? [] // Will be empty
 * ```
 */
export function useCategoriesList(options?: Parameters<typeof useSubjectLists>[1]) {
  return useSubjectLists('category', options)
}
